// Klantplanning → voorstel voor de postproductiewizard (wiki b30c9aec).
// De engine (ai-engine/api/frontdoor/klantplanning.py) leest de mijlpalen uit
// een mail, screenshot of document; hier maken we daar start, deadline,
// feedbackdagen en een notitie voor de planner van. Puur rekenwerk, zodat de
// gebruiker elke regel kan aanpassen en direct ziet wat er zou veranderen.

import { telWerkdagen, volgendeWerkdag, korteDatum, feedbackNodig } from './postproductiePlanning';

export const SOORTEN = [
  { key: 'draaidag', label: 'Draaidag' },
  { key: 'start', label: 'Start postproductie' },
  { key: 'v1', label: 'Versie 1' },
  { key: 'feedback', label: 'Feedback klant' },
  { key: 'v2', label: 'Versie 2' },
  { key: 'v3', label: 'Versie 3' },
  { key: 'definitief', label: 'Definitieve versie' },
  { key: 'oplevering', label: 'Oplevering / deadline' },
  { key: 'overig', label: 'Overig' },
];
export const soortLabel = (key) => (SOORTEN.find((s) => s.key === key) || SOORTEN[SOORTEN.length - 1]).label;

const VERSIES = new Set(['v1', 'v2', 'v3']);
const NOTITIE_KOP = 'Planning van de klant:';

const woorden = (tekst) => String(tekst || '').toLowerCase().split(/[^a-z0-9à-ÿ]+/).filter((w) => w.length > 3);

/** Hoort een door de klant genoemde deliverable bij de deliverable waarvoor de wizard open staat? */
export function hoortBijDeliverable(genoemd, huidig) {
  const a = String(genoemd || '').trim().toLowerCase();
  const b = String(huidig || '').trim().toLowerCase();
  if (!a) return true; // geen deliverable genoemd: geldt voor het hele project
  if (!b) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const eigen = new Set(woorden(b));
  return woorden(a).some((w) => eigen.has(w));
}

/**
 * Engine-mijlpalen → bewerkbare regels. Aan als er een datum is én de regel bij
 * deze deliverable hoort (of bij geen specifieke). Noemt de planning meerdere
 * deliverables, dan staan de regels van de andere zichtbaar maar uit — anders
 * kreeg deze film de deadline van een andere.
 */
export function naarRegels(mijlpalen = [], huidigeDeliverable = '') {
  return (mijlpalen || []).map((m, i) => {
    const eigen = hoortBijDeliverable(m.deliverable, huidigeDeliverable);
    return {
      id: `m${i}`,
      soort: m.soort || 'overig',
      label: m.label || '',
      datum: m.datum || '',
      datumTekst: m.datum_tekst || '',
      zeker: !!m.zeker,
      deliverable: m.deliverable || '',
      andereDeliverable: !eigen,
      aan: !!m.datum && eigen,
    };
  });
}

/**
 * Welke wizardwaarden de aangevinkte regels opleveren. `null` = laat staan.
 *  - start: expliciete start, anders de werkdag na de laatste draaidag;
 *  - deadline: de laatste oplevering, anders de laatste definitieve versie;
 *  - feedbackdagen: de langste termijn die de klant tussen een versie en zijn
 *    feedback neemt (werkdagen, 1–10 zoals het veld in de wizard);
 *  - notitie: alle aangevinkte mijlpalen op datum, voor de planner.
 */
export function voorstelUitRegels(regels = []) {
  const aan = (regels || []).filter((r) => r.aan && /^\d{4}-\d{2}-\d{2}$/.test(r.datum || ''));
  const datums = (soort) => aan.filter((r) => r.soort === soort).map((r) => r.datum).sort();
  const laatste = (lijst) => (lijst.length ? lijst[lijst.length - 1] : null);

  const draaidag = laatste(datums('draaidag'));
  const startDate = datums('start')[0] || (draaidag ? volgendeWerkdag(draaidag) : null);
  const endDate = laatste(datums('oplevering')) || laatste(datums('definitief'));

  let feedbackDays = null;
  const versies = aan.filter((r) => VERSIES.has(r.soort)).map((r) => r.datum).sort();
  datums('feedback').forEach((fb) => {
    const vorige = laatste(versies.filter((v) => v < fb));
    if (!vorige) return;
    const dagen = telWerkdagen(volgendeWerkdag(vorige), fb);
    if (dagen > 0) feedbackDays = Math.max(feedbackDays || 0, dagen);
  });
  if (feedbackDays !== null) feedbackDays = Math.min(10, Math.max(1, feedbackDays));

  const gesorteerd = [...aan].sort((a, b) => a.datum.localeCompare(b.datum));
  const notitie = gesorteerd.length
    ? [NOTITIE_KOP, ...gesorteerd.map((r) => {
      const naam = r.label || soortLabel(r.soort);
      return `- ${korteDatum(r.datum)}: ${naam}${r.deliverable ? ` (${r.deliverable})` : ''}`;
    })].join('\n')
    : '';

  const waarschuwingen = [];
  if (startDate && endDate && startDate > endDate) {
    waarschuwingen.push('De start ligt na de deadline van de klant.');
  }
  const mijlpalen = gesorteerd.map(({ id, soort, label, datum, deliverable }) => ({ id, soort, label, datum, deliverable }));
  return { startDate, endDate, feedbackDays, notitie, waarschuwingen, mijlpalen };
}

/** Notitie in de planningsnotities zetten; een eerdere klantplanning wordt vervangen, eigen tekst blijft. */
export function notitieInvoegen(bestaand, notitie) {
  const zonderOude = String(bestaand || '')
    .split(/\n{2,}/)
    .filter((blok) => !blok.startsWith(NOTITIE_KOP))
    .join('\n\n')
    .trim();
  if (!notitie) return zonderOude;
  return zonderOude ? `${zonderOude}\n\n${notitie}` : notitie;
}

// ── vaste dagen (pins) ────────────────────────────────────────────────────
// Besluit Joris: v1/v2/feedback/oplevering van de klant zijn vaste dagen in de
// planner. Elke pin hangt aan één taak die op of vóór die datum klaar moet zijn.
export const PIN_SOORTEN = ['v1', 'feedback', 'v2', 'v3', 'definitief', 'oplevering'];
const VERSIE_TAAK = {
  v1: /\bv\s*1\b/i, v2: /\bv\s*2\b/i, v3: /\bv\s*3\b/i, definitief: /\bdef\b|definitief/i,
};

/**
 * Welke taak hoort bij deze mijlpaal? Op taaknaam: v1 → de (laatste) taak met
 * "v1", definitief → "DEF"/"definitief", oplevering → de laatste taak, feedback →
 * de versie die de klant op dat moment beoordeelt (de laatste versie-mijlpaal
 * ervoor), anders de eerste taak waarna een feedbackronde komt. null = geen.
 * taken: [{ id, name }] in volgorde.
 */
export function kiesPinTaak(mijlpaal, taken = [], alleMijlpalen = []) {
  if (!mijlpaal || !taken.length) return null;
  const { soort } = mijlpaal;
  if (soort === 'oplevering') return taken[taken.length - 1].id;
  if (VERSIE_TAAK[soort]) {
    const passend = taken.filter((t) => VERSIE_TAAK[soort].test(t.name || ''));
    return passend.length ? passend[passend.length - 1].id : null;
  }
  if (soort === 'feedback') {
    const versie = (alleMijlpalen || [])
      .filter((m) => ['v1', 'v2', 'v3'].includes(m.soort) && m.datum && mijlpaal.datum && m.datum < mijlpaal.datum)
      .sort((a, b) => a.datum.localeCompare(b.datum))
      .pop();
    const viaVersie = versie ? kiesPinTaak(versie, taken, []) : null;
    if (viaVersie) return viaVersie;
    const i = taken.findIndex((t, j) => j < taken.length - 1 && feedbackNodig(t.name, taken[j + 1].name));
    return i >= 0 ? taken[i].id : null;
  }
  return null;
}

/**
 * Overgenomen klantmijlpalen → pins [{ mijlpaalId, taakId, datum, soort, label }].
 * `koppeling` (mijlpaalId → taakId, '' = niet vastzetten) wint van de automatische keuze.
 */
export function klantPinsVoor(mijlpalen = [], taken = [], koppeling = {}) {
  const ids = new Set(taken.map((t) => t.id));
  return (mijlpalen || [])
    .filter((m) => PIN_SOORTEN.includes(m.soort) && /^\d{4}-\d{2}-\d{2}$/.test(m.datum || ''))
    .map((m) => {
      const gekozen = koppeling[m.id];
      const taakId = gekozen !== undefined ? gekozen : kiesPinTaak(m, taken, mijlpalen);
      return { mijlpaalId: m.id, taakId: taakId && ids.has(taakId) ? taakId : '', datum: m.datum, soort: m.soort, label: m.label };
    });
}
